/**
 * The survey behind the dashboard and its simulated answers, shaped like the
 * platform's run results so the study result components render them as-is.
 * Every distribution is derived from `predict`, so the answers agree with the
 * KPIs shown on the Results tab for the same configuration and audience.
 */

import {
  Answer,
  Cell,
  Group,
  Persona,
  Question,
  QuestionResults,
  Section,
  Summary,
  VariableDef,
} from "@/types/resources";
import { QuestionType } from "@/types/utils";

import { applyFilters, DEFAULT_FILTERS, filteredSampleSize } from "./filters";
import { AUDIENCES, FACTORS, KPIS, predict } from "./model";
import { packImageUri } from "./PackPreview";
import { STATES } from "./regions";
import {
  AudienceId,
  Configuration,
  FactorId,
  Filters,
  KpiId,
  Kpis,
  Sentiment,
} from "./types";
import { distribution, verbatims } from "./voice";

/* ── Types ─────────────────────────────────────── */

export interface SurveyBlock {
  questions: Question[];
  section: Section | null;
}

interface QuestionSeed {
  id: string;
  lines?: string[];
  options?: string[];
  section: string | null;
  text: string;
  type: QuestionType;
}

interface SectionSeed {
  description: string;
  /** Factor whose options become the rotated cells. */
  factor: FactorId;
  id: string;
  title: string;
  variable: VariableDef;
}

/* ── Constants ─────────────────────────────────── */

const AGE_RANGES: Record<AudienceId, [number, number]> = {
  all: [18, 65],
  genz: [18, 27],
  millennials: [28, 43],
  nutellaBuyers: [22, 58],
  parents: [29, 47],
  pbHeavy: [20, 62],
};

const CREATED_AT = "2026-09-29T09:00:00Z";

/** Habit questions: share of each audience picking each option (0–100). */
const HABITS: Record<string, Record<AudienceId, number[]>> = {
  media: {
    all: [66, 38, 57, 44, 7],
    genz: [93, 71, 31, 38, 2],
    millennials: [82, 52, 48, 55, 3],
    nutellaBuyers: [70, 44, 58, 49, 5],
    parents: [74, 46, 62, 63, 3],
    pbHeavy: [58, 33, 61, 47, 8],
  },
  retail: {
    all: [57, 33, 29, 26, 11, 6, 17],
    genz: [52, 44, 24, 14, 12, 7, 22],
    millennials: [54, 42, 28, 31, 16, 9, 15],
    nutellaBuyers: [55, 39, 31, 33, 14, 6, 14],
    parents: [66, 41, 30, 38, 9, 5, 12],
    pbHeavy: [61, 30, 34, 35, 13, 8, 15],
  },
  spreads: {
    all: [39, 72, 13, 9],
    genz: [46, 64, 18, 11],
    millennials: [43, 70, 15, 8],
    nutellaBuyers: [100, 58, 21, 0],
    parents: [48, 81, 12, 4],
    pbHeavy: [31, 100, 9, 0],
  },
};

const INTENT_SCALE = [
  "Definitely would buy",
  "Probably would buy",
  "Might or might not",
  "Probably not",
  "Definitely not",
];

const PRICE_ROWS = ["$4.99", "$5.49", "$5.99", "$6.49"];

/** Sentiment of every verbatim handed out so far, keyed by its text. */
const QUOTE_SENTIMENT = new Map<string, Sentiment>();

const QUESTIONS: QuestionSeed[] = [
  {
    id: "spreads",
    options: [
      "Nutella",
      "Peanut butter (any brand)",
      "Another chocolate or hazelnut spread",
      "None of these",
    ],
    section: null,
    text: "Which of these spreads have you bought in the past 3 months?",
    type: "multiple_choice",
  },
  {
    id: "retail",
    options: [
      "Walmart",
      "Target",
      "Kroger (or a Kroger banner)",
      "Costco",
      "Whole Foods",
      "Sprouts",
      "Somewhere else",
    ],
    section: null,
    text: "Where do you usually buy spreads like Nutella or peanut butter?",
    type: "multiple_choice",
  },
  {
    id: "media",
    options: [
      "Scroll Instagram, TikTok or Facebook",
      "Follow food, family or fitness creators",
      "Watch TV or streaming with ads",
      "Use a grocery retailer's app or website",
      "None of these",
    ],
    section: null,
    text: "Which of these do you do in a typical week?",
    type: "multiple_choice",
  },
  {
    id: "packIntent",
    options: INTENT_SCALE,
    section: "pack",
    text: "Take a look at this jar. Nutella is launching a peanut spread in the US. How likely would you be to buy it?",
    type: "single_choice",
  },
  {
    id: "packAppeal",
    options: [
      "Very appealing",
      "Appealing",
      "Neutral",
      "Unappealing",
      "Not at all appealing",
    ],
    section: "pack",
    text: "Overall, how appealing is this product to you?",
    type: "single_choice",
  },
  {
    id: "packFit",
    options: [
      "Fits Nutella perfectly",
      "Fits well",
      "Neutral",
      "Doesn't quite fit",
      "Doesn't fit at all",
    ],
    section: "pack",
    text: "How well does this jar fit with what Nutella stands for?",
    type: "single_choice",
  },
  {
    id: "packTrial",
    options: [
      "Will try at launch",
      "Will try eventually",
      "Might try",
      "Unlikely to try",
      "Won't try",
    ],
    section: "pack",
    text: "When do you think you would first try it?",
    type: "single_choice",
  },
  {
    id: "claimLift",
    options: [
      "Much more likely to buy",
      "Somewhat more likely",
      "No difference",
      "Somewhat less likely",
      "Much less likely",
    ],
    section: "claim",
    text: "The front of the jar reads: {{claim}}. Does this make you more or less likely to buy?",
    type: "single_choice",
  },
  {
    id: "claimBelief",
    options: [
      "Completely believable",
      "Fairly believable",
      "Not sure",
      "Not very believable",
      "Not at all believable",
    ],
    section: "claim",
    text: "How believable is this claim for a Nutella product?",
    type: "single_choice",
  },
  {
    id: "campaignNotice",
    options: [
      "Would definitely notice",
      "Would probably notice",
      "Not sure",
      "Probably not",
      "Definitely not",
    ],
    section: "campaign",
    text: "Here is how the launch would be advertised: {{campaign_idea}}. If you came across this campaign, how likely would you be to notice it?",
    type: "single_choice",
  },
  {
    id: "campaignPull",
    options: [
      "Makes me want to try it",
      "Somewhat interesting",
      "Leaves me indifferent",
      "Puts me off a little",
      "Puts me off",
    ],
    section: "campaign",
    text: "How does this campaign idea make you feel about trying the product?",
    type: "single_choice",
  },
  {
    id: "size",
    options: ["12 oz", "16 oz", "20 oz"],
    section: null,
    text: "Which jar size would you most likely buy?",
    type: "single_choice",
  },
  {
    id: "wtp",
    section: null,
    text: "What is the most you would pay for a 16 oz jar of Nutella Peanut Spread?",
    type: "pricing",
  },
  {
    id: "priceLadder",
    lines: PRICE_ROWS,
    options: INTENT_SCALE,
    section: null,
    text: "At each of these shelf prices, how likely would you be to buy a 16 oz jar?",
    type: "single_select_matrix",
  },
  {
    id: "why",
    section: null,
    text: "In your own words, what makes you want to buy it, or not?",
    type: "open_ended",
  },
];

const SECTIONS: SectionSeed[] = [
  {
    description:
      "Each respondent sees one of the five jar designs, from Nutella-led to peanut-led.",
    factor: "packaging",
    id: "pack",
    title: "Pack design",
    variable: { is_required: true, name: "pack_design", type: "image" },
  },
  {
    description:
      "Each respondent sees one front-of-pack claim, or the jar without a claim.",
    factor: "claim",
    id: "claim",
    title: "Front-of-pack claim",
    variable: { is_required: false, name: "claim", type: "text" },
  },
  {
    description:
      "Each respondent reads one campaign idea, described as the creative brief.",
    factor: "positioning",
    id: "campaign",
    title: "Campaign idea",
    variable: { is_required: true, name: "campaign_idea", type: "text" },
  },
];

const STATE_CITIES: Record<string, string> = {
  CA: "Los Angeles",
  FL: "Tampa",
  GA: "Atlanta",
  IL: "Chicago",
  MI: "Detroit",
  NC: "Charlotte",
  NY: "Brooklyn",
  OH: "Columbus",
  PA: "Philadelphia",
  TX: "Houston",
};

/* ── Survey structure ───────────────────────────── */

/** When the simulated study was created. */
export const createdAt = (): string => CREATED_AT;

/** Number of questions in the survey. */
export const questionCount = (): number => QUESTIONS.length;

/** Total simulated respondents: every audience group, unfiltered. */
export const respondentCount = (): number =>
  AUDIENCES.length * filteredSampleSize(DEFAULT_FILTERS);

/** Questions grouped by section, in questionnaire order. */
export const surveyBlocks = (): SurveyBlock[] => {
  const blocks: SurveyBlock[] = [];
  QUESTIONS.forEach((seed, index) => {
    const question = buildQuestion(seed, index);
    const last = blocks[blocks.length - 1];
    if (last && (last.section?.id ?? null) === seed.section) {
      last.questions.push(question);
      return;
    }
    const sectionSeed = SECTIONS.find((s) => s.id === seed.section);
    blocks.push({
      questions: [question],
      section: sectionSeed ? buildSection(sectionSeed, blocks.length) : null,
    });
  });
  return blocks;
};

/** Index of the cell matching the configuration for a rotated section. */
export const configuredCell = (
  section: Section,
  configuration: Configuration
): number => {
  const seed = SECTIONS.find((s) => s.id === section.id);
  const factor = FACTORS.find((f) => f.id === seed?.factor);
  if (!factor) return 0;
  const index = factor.options.findIndex(
    (o) => o.value === configuration[factor.id]
  );
  return Math.max(0, index);
};

/** Short label per cell for the cell tab bar. */
export const cellLabels = (section: Section): string[] => {
  const seed = SECTIONS.find((s) => s.id === section.id);
  const factor = FACTORS.find((f) => f.id === seed?.factor);
  return (factor?.options ?? []).map((o) => o.label);
};

/* ── Results ────────────────────────────────────── */

/**
 * Simulated results for one question: one group per audience (the national
 * sample first, then each segment), each with `filteredSampleSize` answers
 * whose distribution matches the predicted KPIs. Rotated questions carry one
 * full set of answers per cell; summaries are produced by `summarize` once
 * the caller has narrowed the answers.
 */
export const surveyResults = (
  question: Question,
  section: Section | null,
  configuration: Configuration,
  filters: Filters
): QuestionResults => ({
  groups: AUDIENCES.map((audience, index) => ({
    answers: answersFor(question, section, configuration, audience.id, filters),
    group: buildGroup(audience.id, audience.label, index),
    summary: null,
  })),
  question,
  summary: null,
});

/** Headline, body and optional chart for a set of answers to one question. */
export const summarize = (
  question: Question,
  answers: Answer[],
  scope: string,
  configuration: Configuration
): Summary | null => {
  if (answers.length === 0) return null;
  const n = answers.length;
  const pct = (count: number) => `${Math.round((100 * count) / n)}%`;

  if (question.type === "single_choice") {
    const counts = question.options.map(() => 0);
    answers.forEach(({ answer }) => {
      if (answer.type === "single_choice" && answer.choice !== null)
        counts[answer.choice] += 1;
    });
    const top2 = counts[0] + counts[1];
    const leader = counts.indexOf(Math.max(...counts));
    return {
      headline:
        question.options.length === 5
          ? `${pct(top2)} of ${scope} sit in the top two boxes: “${question.options[0]}” or “${question.options[1]}”.`
          : `“${question.options[leader]}” is the most common answer among ${scope} (${pct(counts[leader])}).`,
      summary:
        question.options.length === 5
          ? `**${pct(counts[0])}** pick “${question.options[0]}”, **${pct(counts[1])}** “${question.options[1]}”. The middle box holds **${pct(counts[2])}**, and **${pct(counts[3] + counts[4])}** lean negative.`
          : question.options
              .map((o, i) => `**${pct(counts[i])}** ${o}`)
              .join(" · "),
      visualization: null,
    };
  }

  if (question.type === "multiple_choice") {
    const counts = question.options.map(() => 0);
    answers.forEach(({ answer }) => {
      if (answer.type === "multiple_choice")
        answer.choices?.forEach((c) => (counts[c] += 1));
    });
    const ranked = question.options
      .map((label, i) => ({ count: counts[i], label }))
      .sort((a, b) => b.count - a.count);
    return {
      headline: `${ranked[0].label} leads among ${scope} at ${pct(ranked[0].count)}, ahead of ${ranked[1].label.toLowerCase()} (${pct(ranked[1].count)}).`,
      summary: ranked
        .slice(0, 4)
        .map((r) => `**${pct(r.count)}** ${r.label}`)
        .join(" · "),
      visualization: null,
    };
  }

  if (question.type === "pricing") {
    const prices = answers
      .map(({ answer }) => (answer.type === "pricing" ? answer.price : null))
      .filter((p): p is number => p !== null)
      .sort((a, b) => a - b);
    const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
    const shelf = Number(configuration.price);
    const accepting = prices.filter((p) => p >= shelf - 0.001).length;
    return {
      headline: `${scope} would pay $${mean.toFixed(2)} on average; ${pct(accepting)} accept the $${shelf.toFixed(2)} shelf price or more.`,
      summary: `Median **$${prices[Math.floor(prices.length / 2)].toFixed(2)}**, range **$${prices[0].toFixed(2)}–$${prices[prices.length - 1].toFixed(2)}**.`,
      visualization: null,
    };
  }

  if (question.type === "single_select_matrix") {
    const top2 = question.lines.map(
      (_, row) =>
        answers.filter(
          ({ answer }) =>
            answer.type === "single_select_matrix" &&
            (answer.choices?.[row] ?? 9) <= 1
        ).length
    );
    return {
      headline: `Purchase intent falls from ${pct(top2[0])} at ${question.lines[0]} to ${pct(top2[top2.length - 1])} at ${question.lines[question.lines.length - 1]} for ${scope}.`,
      summary: question.lines
        .map((line, i) => `**${pct(top2[i])}** at ${line}`)
        .join(" · "),
      visualization: null,
    };
  }

  if (question.type === "open_ended") {
    const buckets = { negative: 0, neutral: 0, positive: 0 };
    answers.forEach(({ answer }) => {
      const text = answer.text ?? "";
      buckets[sentimentOf(text)] += 1;
    });
    return {
      headline: `${pct(buckets.positive)} of ${scope} talk about the product favourably, ${pct(buckets.negative)} push back, mostly on price and brand fit.`,
      summary: `Positive comments centre on the cocoa-and-peanut taste and the Nutella name; negative ones on the shelf price and whether the jar still feels like Nutella. **${pct(buckets.neutral)}** are undecided.`,
      visualization: {
        bar_chart: {
          data: [
            { label: "Positive", value: buckets.positive },
            { label: "Mixed", value: buckets.neutral },
            { label: "Negative", value: buckets.negative },
          ],
        },
        type: "bar_chart",
      },
    };
  }

  return null;
};

/* ── Private: structure ─────────────────────────── */

const buildGroup = (id: string, name: string, order: number): Group => ({
  created_at: CREATED_AT,
  demographics: [],
  description: null,
  id,
  media: [],
  media_urls: [],
  name,
  order,
  panel: null,
  size: 600,
  updated_at: null,
});

const buildQuestion = (seed: QuestionSeed, index: number): Question => ({
  assignment: null,
  carry_forward: null,
  cells: [],
  checked_by: [],
  config: null,
  created_at: CREATED_AT,
  id: seed.id,
  lines: seed.lines ?? [],
  media: [],
  media_urls: [],
  options: seed.options ?? [],
  order: index + 1,
  rules: [],
  section: seed.section,
  targeted_by: [],
  text: seed.text,
  type: seed.type,
  updated_at: null,
});

const buildSection = (seed: SectionSeed, order: number): Section => {
  const factor = FACTORS.find((f) => f.id === seed.factor);
  const cells: Cell[] = (factor?.options ?? []).map((option) => {
    if (seed.variable.type === "image") {
      const uri = packImageUri(option.value);
      return { [seed.variable.name]: uri, [`${seed.variable.name}_url`]: uri };
    }
    const value =
      seed.factor === "claim" && option.value === "none"
        ? "No front-of-pack claim"
        : seed.factor === "positioning" && option.description
          ? `${option.label}. ${option.description}`
          : option.label;
    return { [seed.variable.name]: value };
  });
  return {
    cells,
    config: {
      cells_per_respondent: 1,
      items_per_cell: 1,
      variables: [seed.variable],
    },
    created_at: CREATED_AT,
    description: seed.description,
    id: seed.id,
    order,
    title: seed.title,
    updated_at: null,
    use_case: null,
  };
};

/* ── Private: answers ───────────────────────────── */

const answersFor = (
  question: Question,
  section: Section | null,
  configuration: Configuration,
  audience: AudienceId,
  filters: Filters
): Answer[] => {
  const n = filteredSampleSize(filters);
  const random = rng(
    hash(`${question.id}|${audience}|${JSON.stringify(filters)}`)
  );
  const personas = Array.from({ length: n }, (_, i) =>
    buildPersona(`${audience}-${i}`, audience, filters, random)
  );
  const kpisFor = (overrides: Partial<Configuration>): Kpis => {
    const cfg = { ...configuration, ...overrides };
    return applyFilters(predict(cfg, audience), cfg, filters);
  };

  const seed = SECTIONS.find((s) => s.id === section?.id);
  const factor = FACTORS.find((f) => f.id === seed?.factor);
  if (seed && factor) {
    // One full set of answers per cell, distribution read with that option.
    return factor.options.flatMap((option, cell) => {
      const kpis = kpisFor({ [factor.id]: option.value });
      const shares = rotatedShares(question.id, kpis, kpisFor, option.value);
      const choices = allocate(shares, n, random);
      return personas.map((persona, i) =>
        choiceAnswer(persona, choices[i], cell)
      );
    });
  }

  const kpis = kpisFor({});
  switch (question.id) {
    case "spreads":
    case "retail":
    case "media":
      return personas.map((persona) =>
        multiAnswer(
          persona,
          HABITS[question.id][audience],
          question.options.length - 1,
          random
        )
      );
    case "size": {
      const intents = FACTORS.find((f) => f.id === "packSize")!.options.map(
        (o) => kpisFor({ packSize: o.value }).purchaseIntent
      );
      const total = intents.reduce((a, b) => a + b, 0);
      const shares = intents.map((v) => (100 * v) / total);
      const choices = allocate(shares, n, random);
      return personas.map((persona, i) =>
        choiceAnswer(persona, choices[i], null)
      );
    }
    case "wtp": {
      const bins = distribution(kpi("wtp"), kpis.wtp);
      const picks = allocate(
        bins.map((b) => b.share),
        n,
        random
      );
      return personas.map((persona, i) => ({
        answer: {
          currency: "USD",
          price: Number(bins[picks[i]].label.slice(1)),
          text: null,
          translated_text: null,
          type: "pricing",
        },
        cell: null,
        confidence_score: null,
        persona,
        sources: [],
      }));
    }
    case "priceLadder": {
      const rows = PRICE_ROWS.map((price) =>
        allocate(
          distribution(
            kpi("purchaseIntent"),
            kpisFor({ price: price.slice(1) }).purchaseIntent
          ).map((b) => b.share),
          n,
          random
        )
      );
      return personas.map((persona, i) => ({
        answer: {
          choices: rows.map((row) => row[i]),
          text: null,
          translated_text: null,
          type: "single_select_matrix",
        },
        cell: null,
        confidence_score: null,
        persona,
        sources: [],
      }));
    }
    case "why": {
      const quotes = verbatims(
        "purchaseIntent",
        configuration,
        audience,
        kpis,
        48
      );
      quotes.forEach((q) => QUOTE_SENTIMENT.set(q.quote, q.sentiment));
      return personas.map((persona, i) => {
        const quote = quotes[i % quotes.length];
        return {
          answer: {
            text: quote.quote,
            translated_text: null,
            type: "open_ended",
          },
          cell: null,
          confidence_score: null,
          persona: {
            ...persona,
            general: {
              ...persona.general,
              age: quote.age,
              gender: quote.gender === "Female" ? "female" : "male",
            },
          },
          sources: [],
        };
      });
    }
    default:
      return [];
  }
};

/** Shares for a rotated question, given the KPIs read with the cell option. */
const rotatedShares = (
  questionId: string,
  kpis: Kpis,
  kpisFor: (overrides: Partial<Configuration>) => Kpis,
  option: string
): number[] => {
  switch (questionId) {
    case "packIntent":
      return fivePoint("purchaseIntent", kpis);
    case "packAppeal":
      return fivePoint("appeal", kpis);
    case "packFit":
      return fivePoint("brandFit", kpis);
    case "packTrial":
      return fivePoint("trial", kpis);
    case "claimLift": {
      // Lift vs. the bare jar: positive deltas push mass to "more likely".
      const delta =
        kpis.purchaseIntent - kpisFor({ claim: "none" }).purchaseIntent;
      const top2 = Math.max(
        4,
        Math.min(90, option === "none" ? 8 : 36 + 6 * delta)
      );
      const rest = 100 - top2;
      return [top2 * 0.32, top2 * 0.68, rest * 0.62, rest * 0.26, rest * 0.12];
    }
    case "claimBelief":
      return option === "none"
        ? [30, 40, 22, 6, 2]
        : fivePoint("brandFit", kpis);
    case "campaignNotice":
      return fivePoint("awareness", kpis);
    case "campaignPull":
      return fivePoint("trial", kpis);
    default:
      return [20, 20, 20, 20, 20];
  }
};

const fivePoint = (id: KpiId, kpis: Kpis): number[] =>
  distribution(kpi(id), kpis[id]).map((b) => b.share);

const kpi = (id: KpiId) => KPIS.find((k) => k.id === id) ?? KPIS[0];

const choiceAnswer = (
  persona: Persona,
  choice: number,
  cell: number | null
): Answer => ({
  answer: { choice, text: null, translated_text: null, type: "single_choice" },
  cell,
  confidence_score: null,
  persona,
  sources: [],
});

/** Independent picks per option; the last option is exclusive ("none"). */
const multiAnswer = (
  persona: Persona,
  shares: number[],
  noneIndex: number,
  random: () => number
): Answer => {
  const picked = shares
    .map((share, i) => (i !== noneIndex && random() * 100 < share ? i : -1))
    .filter((i) => i >= 0);
  const choices = picked.length > 0 ? picked : [noneIndex];
  return {
    answer: {
      choices,
      text: null,
      translated_text: null,
      type: "multiple_choice",
    },
    cell: null,
    confidence_score: null,
    persona,
    sources: [],
  };
};

/* ── Private: summaries ─────────────────────────── */

const sentimentOf = (text: string): Sentiment => {
  const known = QUOTE_SENTIMENT.get(text);
  if (known) return known;
  const lower = text.toLowerCase();
  if (/(not|won't|wouldn't|too|expensive|no |doesn't|don't|pass)/.test(lower))
    return "negative";
  if (/(love|great|yes|definitely|perfect|excited|buy|delicious)/.test(lower))
    return "positive";
  return "neutral";
};

/* ── Private: personas ──────────────────────────── */

const buildPersona = (
  id: string,
  audience: AudienceId,
  filters: Filters,
  random: () => number
): Persona => {
  const [lo, hi] = ageRange(audience, filters);
  const age = lo + Math.floor(random() * (hi - lo + 1));
  const states = STATES.filter(
    (s) => filters.region === "all" || s.region === filters.region
  );
  const state = states[Math.floor(random() * states.length)];
  const urbanicity =
    filters.urbanicity === "all"
      ? (["urban", "suburban", "suburban", "rural"] as const)[
          Math.floor(random() * 4)
        ]
      : filters.urbanicity;
  const children = audience === "parents" ? 1 + Math.floor(random() * 3) : 0;
  return {
    behaviors: [],
    education: { level: "bachelor", specialization: null },
    family: {
      children_ages: Array.from({ length: children }, () =>
        Math.floor(random() * 12)
      ),
      children_count: children,
      household_size: children > 0 ? "4" : random() < 0.5 ? "2" : "1",
      marital_status: children > 0 || random() < 0.4 ? "married" : "single",
      spouse_age: null,
    },
    general: {
      age,
      gender: random() < 0.52 ? "female" : "male",
      language: "en",
      race_ethnicity: "",
    },
    id,
    occupation: {
      currency: "USD",
      employment_status: "full_time",
      income_range: "medium",
      title: null,
      years_in_role: Math.max(0, Math.floor((age - 22) * random())),
    },
    personality: {
      speaking_tone: [],
      speaking_vocabulary: "",
      traits: [],
      values: [],
    },
    residency: {
      city: STATE_CITIES[state.abbr] ?? null,
      country: "United States",
      country_code: "us",
      county: null,
      county_code: null,
      region: null,
      state: state.name,
      state_code: state.abbr,
      type: urbanicity,
    },
  };
};

const ageRange = (audience: AudienceId, filters: Filters): [number, number] => {
  const [lo, hi] = AGE_RANGES[audience];
  if (filters.age === "all") return [lo, hi];
  const [flo, fhi] =
    filters.age === "18-34"
      ? [18, 34]
      : filters.age === "35-54"
        ? [35, 54]
        : [55, 70];
  const a = Math.max(lo, flo);
  const b = Math.min(hi, fhi);
  return a <= b ? [a, b] : [flo, fhi];
};

/* ── Private: randomness ────────────────────────── */

const hash = (text: string): number => {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/**
 * `n` option indices whose counts match the shares exactly (largest
 * remainder), shuffled so respondents are not grouped by answer.
 */
const allocate = (
  shares: number[],
  n: number,
  random: () => number
): number[] => {
  const total = shares.reduce((a, b) => a + b, 0);
  const exact = shares.map((s) => (n * s) / total);
  const counts = exact.map(Math.floor);
  let remaining = n - counts.reduce((a, b) => a + b, 0);
  exact
    .map((v, i) => ({ frac: v - counts[i], i }))
    .sort((a, b) => b.frac - a.frac)
    .forEach(({ i }) => {
      if (remaining > 0) {
        counts[i] += 1;
        remaining -= 1;
      }
    });
  const out = counts.flatMap((count, i) => Array(count).fill(i) as number[]);
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const rng = (seed: number) => {
  let s = seed || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
